/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { getId } from '@fluentui/utilities';
import { transaction } from 'mobx';
import { CluUtteranceSwagger } from 'src/clu/core/services/cluUtteranceSwagger';
import { getCluEntityService } from 'src/clu/core/services/entities/cluEntityService';
import { CluPrebuiltEntity } from 'src/clu/core/types/CluPrebuiltEntity';
import { cluEntityStoreToEntitySwagger } from 'src/clu/modules/handlers/entities/cluEntityMappers';
import { getCluProjectExportMetaDataSwagger } from 'src/clu/modules/handlers/projects/cluProjectMappers';
import { EntityStore, PrebuiltEntityCapability, RegexEntityCapability, SublistEntityCapability } from 'src/core/types/EntityStore';
import { EntityComponents, EntitySublistSwagger, PrebuiltEntitySwagger } from 'src/core/types/EntitySwagger';
import { HandlerGroupFactoryDeps, HandlerParams } from 'src/core/types/handler';
import { getNextAbleResults } from 'src/core/utils/batchPromises';

export type CluEntityHandlerGroup = ReturnType<typeof createCluEntityHandlerGroup>;

export const createCluEntityHandlerGroup = (dependencies: HandlerGroupFactoryDeps) => {
    const { rootStore } = dependencies;

    const addCluEntity = async ({ name }: HandlerParams<Pick<EntityStore, 'name'>>) => {
        const { currentCluProjectStore } = rootStore;
        const entitiesData = [...currentCluProjectStore.jsonData.entities, { category: name }];

        await getCluEntityService().updateEntities({
            entitiesData,
            projectRequiredMetaData: getCluProjectExportMetaDataSwagger(currentCluProjectStore)
        });

        transaction(() => {
            currentCluProjectStore.patchJsonData({ entities: entitiesData });
            currentCluProjectStore.entityStoreHouse.add(new EntityStore({ name }));
        });
    };

    const deleteCluEntities = async ({ entityNames }: HandlerParams<{ entityNames: string[] }>) => {
        const { currentCluProjectStore } = rootStore;

        const newEntities = currentCluProjectStore.jsonData.entities.filter(e => !entityNames.includes(e.category));

        const newUtterances = currentCluProjectStore.jsonData.utterances.map<CluUtteranceSwagger>(u => ({
            text: u.text,
            intent: u.intent,
            dataset: u.dataset,
            language: u.language,
            entities: u.entities.filter(e => !entityNames.includes(e.category))
        }));

        await getCluEntityService().updateEntities({
            utterances: newUtterances,
            entitiesData: newEntities,
            projectRequiredMetaData: getCluProjectExportMetaDataSwagger(currentCluProjectStore)
        });

        transaction(() => {
            currentCluProjectStore.patchJsonData({ entities: newEntities, utterances: newUtterances });
            currentCluProjectStore.entityStoreHouse.batchRemoveByIds(entityNames);
        });
    };

    const updateCluEntityCombinationOption = async ({
        compositionSetting,
        name
    }: HandlerParams<Pick<EntityStore, 'name' | 'compositionSetting'>>) => {
        const { currentCluProjectStore } = rootStore;
        const entityStore = currentCluProjectStore.entityStoreHouse.findById(name);
        const entitiesData = currentCluProjectStore.jsonData.entities.map(entity =>
            entity.category === name ? cluEntityStoreToEntitySwagger({ ...entityStore, compositionSetting }) : entity
        );

        await getCluEntityService().updateEntities({
            entitiesData,
            projectRequiredMetaData: getCluProjectExportMetaDataSwagger(currentCluProjectStore)
        });

        transaction(() => {
            currentCluProjectStore.patchJsonData({ entities: entitiesData });
            entityStore.setCompositionSetting(compositionSetting);
        });
    };

    const updateCluEntitySublistCapability = async ({
        sublist,
        name
    }: HandlerParams<Pick<EntityStore, 'name'> & { sublist: SublistEntityCapability }>) => {
        const { currentCluProjectStore } = rootStore;
        const entityStore = currentCluProjectStore.entityStoreHouse.findById(name);

        const newSublists = entityStore.sublists.map(s => (s.id === sublist.id ? sublist : s));
        const entitiesData = currentCluProjectStore.jsonData.entities.map(entity =>
            entity.category === name ? cluEntityStoreToEntitySwagger({ ...entityStore, sublists: newSublists }) : entity
        );

        await getCluEntityService().updateEntities({
            entitiesData,
            projectRequiredMetaData: getCluProjectExportMetaDataSwagger(currentCluProjectStore)
        });

        transaction(() => {
            currentCluProjectStore.patchJsonData({ entities: entitiesData });
            entityStore.setSublists(newSublists);
        });
    };

    const addCluEntitySublistCapability = async ({
        sublist,
        name
    }: HandlerParams<Pick<EntityStore, 'name'> & { sublist: EntitySublistSwagger }>) => {
        const { currentCluProjectStore } = rootStore;
        const entityStore = currentCluProjectStore.entityStoreHouse.findById(name);
        const newSublists = [{ ...sublist, id: getId() }].concat(entityStore.sublists);

        const entitiesData = currentCluProjectStore.jsonData.entities.map(entity =>
            entity.category === name ? cluEntityStoreToEntitySwagger({ ...entityStore, sublists: newSublists }) : entity
        );

        await getCluEntityService().updateEntities({
            entitiesData,
            projectRequiredMetaData: getCluProjectExportMetaDataSwagger(currentCluProjectStore)
        });

        transaction(() => {
            currentCluProjectStore.patchJsonData({ entities: entitiesData });
            entityStore.setSublists(newSublists);
        });
    };

    const updateCluEntityRegexCapability = async ({
        regex,
        name
    }: HandlerParams<Pick<EntityStore, 'name'> & { regex: RegexEntityCapability }>) => {
        const { currentCluProjectStore } = rootStore;
        const entityStore = currentCluProjectStore.entityStoreHouse.findById(name);

        const newRegexList = entityStore.regexList.map(s => (s.id === regex.id ? regex : s));
        const entitiesData = currentCluProjectStore.jsonData.entities.map(entity =>
            entity.category === name ? cluEntityStoreToEntitySwagger({ ...entityStore, regexList: newRegexList }) : entity
        );

        await getCluEntityService().updateEntities({
            entitiesData,
            projectRequiredMetaData: getCluProjectExportMetaDataSwagger(currentCluProjectStore)
        });

        transaction(() => {
            currentCluProjectStore.patchJsonData({ entities: entitiesData });
            entityStore.setRegexList(newRegexList);
        });
    };

    const addCluEntityRegexCapability = async ({
        regex,
        name
    }: HandlerParams<Pick<EntityStore, 'name'> & { regex: RegexEntityCapability }>) => {
        const { currentCluProjectStore } = rootStore;
        const entityStore = currentCluProjectStore.entityStoreHouse.findById(name);
        const newRegexList = [{ ...regex, id: getId() }].concat(entityStore.regexList);

        const entitiesData = currentCluProjectStore.jsonData.entities.map(entity =>
            entity.category === name ? cluEntityStoreToEntitySwagger({ ...entityStore, regexList: newRegexList }) : entity
        );

        await getCluEntityService().updateEntities({
            entitiesData,
            projectRequiredMetaData: getCluProjectExportMetaDataSwagger(currentCluProjectStore)
        });

        transaction(() => {
            currentCluProjectStore.patchJsonData({ entities: entitiesData });
            entityStore.setRegexList(newRegexList);
        });
    };

    const updateCluEntityPrebuiltCapability = async ({
        prebuilt,
        name
    }: HandlerParams<Pick<EntityStore, 'name'> & { prebuilt: PrebuiltEntityCapability }>) => {
        const { currentCluProjectStore } = rootStore;
        const entityStore = currentCluProjectStore.entityStoreHouse.findById(name);

        const newPrebuilts = entityStore.prebuilts.map(p => (p.id === prebuilt.id ? prebuilt : p));
        const entitiesData = currentCluProjectStore.jsonData.entities.map(entity =>
            entity.category === name ? cluEntityStoreToEntitySwagger({ ...entityStore, prebuilts: newPrebuilts }) : entity
        );

        await getCluEntityService().updateEntities({
            entitiesData,
            projectRequiredMetaData: getCluProjectExportMetaDataSwagger(currentCluProjectStore)
        });

        transaction(() => {
            currentCluProjectStore.patchJsonData({ entities: entitiesData });
            entityStore.setPrebuilts(newPrebuilts);
        });
    };

    const updateCluEntityRequired = async ({
        isRequired,
        category,
        name
    }: HandlerParams<Pick<EntityStore, 'name'> & { isRequired: boolean; category: EntityComponents }>) => {
        const { currentCluProjectStore } = rootStore;
        const entityStore = currentCluProjectStore.entityStoreHouse.findById(name);
        let newRequiredComponents: string[] = [...entityStore.requiredComponents];
        if (isRequired) {
            const categoryIndex = newRequiredComponents.indexOf(category);
            if (categoryIndex === -1) {
                newRequiredComponents = [...newRequiredComponents, category];
            }
        } else {
            const categoryIndex = newRequiredComponents.indexOf(category);
            if (categoryIndex > -1) {
                newRequiredComponents.splice(categoryIndex, 1);
            }
        }

        const entitiesData = currentCluProjectStore.jsonData.entities.map(entity =>
            entity.category === name ? cluEntityStoreToEntitySwagger({ ...entityStore, requiredComponents: newRequiredComponents }) : entity
        );

        await getCluEntityService().updateEntities({
            entitiesData,
            projectRequiredMetaData: getCluProjectExportMetaDataSwagger(currentCluProjectStore)
        });

        transaction(() => {
            currentCluProjectStore.patchJsonData({ entities: entitiesData });
            entityStore.setRequiredComponents(newRequiredComponents);
        });
    };

    const addCluEntityPrebuiltCapability = async ({
        prebuilt,
        name
    }: HandlerParams<Pick<EntityStore, 'name'> & { prebuilt: PrebuiltEntityCapability }>) => {
        const { currentCluProjectStore } = rootStore;
        const entityStore = currentCluProjectStore.entityStoreHouse.findById(name);
        const newPrebuilts = [{ ...prebuilt, id: getId() }].concat(entityStore.prebuilts);

        const entitiesData = currentCluProjectStore.jsonData.entities.map(entity =>
            entity.category === name ? cluEntityStoreToEntitySwagger({ ...entityStore, prebuilts: newPrebuilts }) : entity
        );

        await getCluEntityService().updateEntities({
            entitiesData,
            projectRequiredMetaData: getCluProjectExportMetaDataSwagger(currentCluProjectStore)
        });

        transaction(() => {
            currentCluProjectStore.patchJsonData({ entities: entitiesData });
            entityStore.setPrebuilts(newPrebuilts);
        });
    };

    const deleteCluEntityCapabilities = async ({ ids, name }: HandlerParams<Pick<EntityStore, 'name'> & { ids: string[] }>) => {
        const { currentCluProjectStore } = rootStore;
        const entityStore = currentCluProjectStore.entityStoreHouse.findById(name);
        const newSublists = entityStore.sublists.filter(s => !ids.includes(s.id));
        const newPrebuilts = entityStore.prebuilts.filter(p => !ids.includes(p.id));
        const newRegexList = entityStore.regexList.filter(p => !ids.includes(p.id));

        const entitiesData = currentCluProjectStore.jsonData.entities.map(entity =>
            entity.category === name
                ? cluEntityStoreToEntitySwagger({ ...entityStore, sublists: newSublists, prebuilts: newPrebuilts })
                : entity
        );

        await getCluEntityService().updateEntities({
            entitiesData,
            projectRequiredMetaData: getCluProjectExportMetaDataSwagger(currentCluProjectStore)
        });

        // update required components
        const newRequiredComponents: string[] = [...entityStore.requiredComponents];
        if (newSublists.length === 0) {
            const listIndex = newRequiredComponents.indexOf('list');
            if (listIndex !== -1) {
                newRequiredComponents.splice(listIndex, 1);
            }
        }

        if (newPrebuilts.length === 0) {
            const prebuiltsIndex = newRequiredComponents.indexOf('prebuilts');
            if (prebuiltsIndex !== -1) {
                newRequiredComponents.splice(prebuiltsIndex, 1);
            }
        }

        transaction(() => {
            currentCluProjectStore.patchJsonData({ entities: entitiesData });
            entityStore.setSublists(newSublists);
            entityStore.setPrebuilts(newPrebuilts);
            entityStore.setRequiredComponents(newRequiredComponents);
            entityStore.setRegexList(newRegexList);
        });
    };

    const getCluAvailablePrebuilts = async ({ allPrebuiltsList }: HandlerParams<{ allPrebuiltsList: CluPrebuiltEntity[] }>) => {
        const { currentCluProjectStore } = rootStore;

        const prebuiltsSwagger = await getNextAbleResults<'value', PrebuiltEntitySwagger>(
            (nextLink: string) =>
                getCluEntityService().getAvailablePrebuilts({
                    nextLink,
                    projectCulture: currentCluProjectStore.culture,
                    isMultiLingual: currentCluProjectStore.isMultiLingual
                }),
            'value'
        );

        prebuiltsSwagger.forEach(p => {
            allPrebuiltsList.push({ category: p.category, description: p.description, examples: p.examples.split(',') });
        });
    };

    return {
        addCluEntity,
        deleteCluEntities,
        updateCluEntityCombinationOption,
        updateCluEntitySublistCapability,
        addCluEntitySublistCapability,
        updateCluEntityRegexCapability,
        addCluEntityRegexCapability,
        updateCluEntityPrebuiltCapability,
        updateCluEntityRequired,
        addCluEntityPrebuiltCapability,
        deleteCluEntityCapabilities,
        getCluAvailablePrebuilts
    };
};
